'use client';

import React, { useEffect, useState } from 'react';

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full flex items-center justify-start overflow-hidden"
      style={{
        minHeight: '100vh',
        background: `url('https://www.gorgisairheat.com/assets/img/heroImage.jpg') top center / cover no-repeat`,
        paddingTop: '100px',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#1e1e1e]/60" />
      {/* Gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1e1e1e]/80 via-[#1e1e1e]/40 to-transparent" />
      {/* Gold accent line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#b5973a] to-transparent" />

      <div className="relative z-10 container mx-auto px-6 lg:px-12 py-20">
        <div className="max-w-3xl">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 bg-[#b5973a]/20 border border-[#b5973a]/60 rounded-full px-4 py-2 mb-6 transition-all duration-700 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-[#b5973a] animate-pulse" />
            <span className="text-[#d4aa4a] font-poppins text-xs font-semibold uppercase tracking-widest">
              San Diego&apos;s Trusted HVAC Experts
            </span>
          </div>

          {/* Heading */}
          <h1
            className={`font-poppins font-bold text-white leading-tight mb-6 transition-all duration-700 delay-100 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ fontSize: 'clamp(32px, 5vw, 60px)' }}
          >
            Gorgis
            <span className="text-[#b5973a] block sm:inline"> Air Conditioning</span>
            <span className="block">&amp; Heating Inc.</span>
          </h1>

          {/* Description */}
          <p
            className={`text-[#eee] text-lg md:text-xl mb-4 leading-relaxed transition-all duration-700 delay-200 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <strong className="text-white">Locally owned &amp; operated</strong> — San Diego&apos;s go-to HVAC experts for over{' '}
            <strong className="text-white">10 years</strong>. Fast, reliable service. Unbeatable rates. Your comfort is our mission.
          </p>

          <p
            className={`text-[#b5973a] font-semibold text-lg mb-4 transition-all duration-700 delay-300 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            ✦ Request a Complimentary In-Home Consultation
          </p>

          {/* First Appointment Free Banner + Financing Promo */}
          <div
            className={`relative mb-8 transition-all duration-700 delay-350 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="flex flex-col sm:flex-row gap-4">
              {/* First Appointment Free */}
              <div className="relative inline-block flex-1">
                <div className="absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-[#b5973a] via-[#d4aa4a] to-[#b5973a] opacity-80 animate-pulse" />
                <div className="relative bg-[#1e1e1e] rounded-2xl px-6 py-4 flex flex-col sm:flex-row items-center gap-4 h-full">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#b5973a] font-semibold mb-1">Limited Offer</span>
                    <p className="text-white font-extrabold text-xl sm:text-2xl leading-tight tracking-tight">
                      Your First Appointment is{' '}
                      <span className="text-[#b5973a] relative">
                        Always FREE
                        <span className="absolute -bottom-0.5 left-0 w-full h-[2px] bg-[#b5973a]/60 rounded-full" />
                      </span>
                    </p>
                    <p className="text-white/60 text-xs mt-1.5 font-sans">No hidden fees — just honest, expert service from day one.</p>
                  </div>
                  <div className="shrink-0 bg-[#b5973a]/20 border border-[#b5973a]/40 rounded-xl px-4 py-2 text-center">
                    <span className="block text-[#b5973a] font-extrabold text-2xl leading-none">$0</span>
                    <span className="block text-white/50 text-[10px] uppercase tracking-widest mt-0.5">First Visit</span>
                  </div>
                </div>
              </div>

              {/* 24-Month Zero Interest Financing */}
              <div className="relative inline-block flex-1">
                <div className="absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-[#b5973a] via-[#d4aa4a] to-[#b5973a] opacity-80 animate-pulse" />
                <div className="relative bg-[#1e1e1e] rounded-2xl px-6 py-4 flex flex-col items-start gap-1 h-full justify-center">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#b5973a] font-semibold">Limited Time Offer</span>
                  <p className="text-white font-extrabold text-xl sm:text-2xl leading-tight tracking-tight">
                    0% Interest Financing
                  </p>
                  <p className="text-[#b5973a] font-semibold text-sm">24 Months on New Installations</p>
                  <p className="text-white/60 text-xs font-sans">Upgrade your comfort — pay over time with ease.</p>
                  <p className="text-white/30 text-[9px] mt-0.5">*Restrictions apply. Subject to credit approval.</p>
                </div>
              </div>

              {/* Slogan Card */}
              <div className="relative inline-block flex-1">
                <div className="absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-[#b5973a] via-[#d4aa4a] to-[#b5973a] opacity-80 animate-pulse" />
                <div className="relative bg-[#1e1e1e] rounded-2xl px-6 py-4 flex flex-col items-center justify-center gap-2 h-full text-center">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#b5973a] font-semibold">Our Promise</span>
                  <p className="text-white font-extrabold text-xl sm:text-2xl leading-tight tracking-tight">
                    Get the{' '}
                    <span className="text-[#b5973a] relative">
                      Gorgeous
                      <span className="absolute -bottom-0.5 left-0 w-full h-[2px] bg-[#b5973a]/60 rounded-full" />
                    </span>{' '}
                    Deal with Gorgis
                  </p>
                  <p className="text-white/60 text-xs font-sans">Quality service, unbeatable value — every time.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div
            className={`flex flex-wrap gap-4 transition-all duration-700 delay-400 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <a
              href="https://book.housecallpro.com/book/Gorgis-Air-conditioning-and-heating/8d64616590bd4fe49c1a93f43f1cbfae"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#b5973a] hover:bg-[#d4aa4a] text-white font-poppins font-bold uppercase tracking-wider px-8 py-4 rounded-full text-sm transition-all duration-300 shadow-lg shadow-[#b5973a]/40 book-btn-pulse"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Book Online Now
            </a>
            <a
              href="tel:+16192773287"
              className="inline-flex items-center gap-2 border-2 border-[#b5973a] text-white hover:bg-[#b5973a] font-poppins font-bold uppercase tracking-wider px-8 py-4 rounded-full text-sm transition-all duration-300"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call (619) 277-3287
            </a>
          </div>

          {/* Trust badges */}
          <div
            className={`flex flex-wrap gap-6 mt-10 transition-all duration-700 delay-500 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {[
              { icon: '🏆', text: '10+ Years Experience' },
              { icon: '✅', text: '100% Satisfaction Guarantee' },
              { icon: '⚡', text: 'Same Day Service' },
              { icon: '🆓', text: '1st Appointment FREE' },
            ]?.map((badge) => (
              <div key={badge?.text} className="flex items-center gap-2 bg-[#1e1e1e]/50 border border-[#b5973a]/30 rounded-full px-3 py-1">
                <span className="text-lg">{badge?.icon}</span>
                <span className="text-white/90 text-sm font-sans">{badge?.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/50 text-xs font-poppins uppercase tracking-widest">Scroll</span>
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#b5973a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </section>
  );
}
